/**
 * BotConfigContext — hoists the bot-config draft (GET/PUT `/api/bot/{id}/config`
 * + unsaved edits) into a provider that lives in the persistent bot-settings
 * layout route, NOT inside the Configure page.
 *
 * Why: under React Router, navigating between bot-settings pages
 * (Config → Operate → Config) unmounts/remounts the leaf page but keeps the
 * layout (and this provider) mounted. So the loaded config and any unsaved
 * edits survive the round-trip without a refetch.
 *
 * Lazy fetch: the provider does NOT fetch on mount. It fetches once, the first
 * time a consumer calls `useBotConfigContext()` (i.e. when Configure mounts).
 * Sibling pages (Operate/CallLogs/BotHero) never consume it, so they trigger
 * no extra config request — preserving current behavior. Mount the provider
 * with `key={botId}` so switching bots gets a fresh draft.
 *
 * It also owns the WORKFLOW GRAPH drafts (`useGraphDrafts`), for the same
 * reason and with the same lifetime: graph edits are unpublished until the
 * operator clicks "Publish changes", exactly like every other setting. Keeping
 * them here rather than in Configure or WorkflowEditor means an edit survives
 * both a tab switch inside /config — which unmounts the lazily-loaded canvas —
 * and a round-trip to a sibling bot page. It also means Publish can write a
 * dirty graph while its editor is unmounted.
 */
import React, { createContext, memo, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useBotConfig, UseBotConfigResult } from './useBotConfig';
import type { BotConversationGraph } from '../pages/WorkflowEditor';
import {
  readGraphDrafts,
  reloadPage,
  subscribeBotPublished,
  writeGraphDrafts,
  type StoredGraphDraft,
} from './draftStorage';

/**
 * Unpublished conversation-graph edits, keyed by workflow id.
 *
 * `graphs` is the editor's CURRENT state whether or not it differs from the
 * server — the sandbox/simulations rails test that, and they must keep working
 * on a clean graph. `dirtyIDs` is the subset Publish actually writes.
 */
export interface GraphDraftStore {
  /** Current editor state per workflow id (dirty or not). */
  graphs: Record<string, BotConversationGraph>;
  /** Workflow ids whose editor state differs from what the server holds. */
  dirtyIDs: string[];
  /** True iff any workflow has unpublished changes. */
  dirty: boolean;
  /**
   * The published graph each dirty workflow is measured against, for the
   * unpublished-changes summary. Only dirty ids appear; parsed once per change.
   */
  baselines: Record<string, BotConversationGraph>;
  /**
   * Record what the server holds for a workflow — called by the editor right
   * after it loads a head graph. A dirty draft is preserved (only its baseline
   * moves); a clean entry adopts the freshly loaded graph.
   */
  setBaseline: (workflowID: string, graph: BotConversationGraph) => void;
  /** Stage the editor's current state for a workflow. */
  setDraft: (workflowID: string, graph: BotConversationGraph) => void;
  /** Rebase baselines onto current state after a successful publish. */
  commit: (workflowIDs: string[]) => void;
}

interface GraphEntry {
  graph: BotConversationGraph;
  /** `JSON.stringify(graph)` — cached so dirty checks are string compares. */
  json: string;
  /** JSON of the server's version; null until a load has established it. */
  baseline: string | null;
}

interface BotConfigContextValue {
  ctl: UseBotConfigResult;
  /** Idempotent — fetches the config exactly once across the provider's life. */
  ensureLoaded: () => void;
  graphDrafts: GraphDraftStore;
}

const Ctx = createContext<BotConfigContextValue | null>(null);

function useBotConfigValue(botId: string): BotConfigContextValue {
  const ctl = useBotConfig(botId, { autoFetch: false });
  const { refresh } = ctl;
  const requestedRef = useRef(false);
  const ensureLoaded = useCallback(() => {
    if (requestedRef.current) return;
    requestedRef.current = true;
    void refresh();
  }, [refresh]);

  // Hydrate unpublished graph edits persisted by a previous page load. Each
  // restored entry carries its stored baseline, so when the editor loads the
  // head graph and calls setBaseline it takes the "dirty → keep the edit,
  // move the baseline" path instead of adopting the server graph.
  const [graphEntries, setGraphEntries] = useState<Record<string, GraphEntry>>(() => {
    const out: Record<string, GraphEntry> = {};
    for (const [id, stored] of Object.entries(readGraphDrafts(botId))) {
      const graph = stored.graph as BotConversationGraph;
      out[id] = { graph, json: JSON.stringify(graph), baseline: stored.baseline };
    }
    return out;
  });

  // Persist the dirty subset (with baselines) so a reload restores it; the key
  // is removed once every workflow is clean — i.e. after Publish commits.
  useEffect(() => {
    const dirty: Record<string, StoredGraphDraft> = {};
    for (const [id, e] of Object.entries(graphEntries)) {
      if (e.baseline !== null && e.json !== e.baseline) dirty[id] = { graph: e.graph, baseline: e.baseline };
    }
    writeGraphDrafts(botId, dirty);
  }, [botId, graphEntries]);

  // Another tab published this bot. Anything this tab holds for it — the saved
  // snapshot, the draft, graph baselines — is now stale, so reload rather than
  // let the operator keep editing (and later re-publish) an outdated config.
  // Hosts that never loaded the config have nothing stale and stay put.
  useEffect(
    () => subscribeBotPublished(botId, () => {
      if (requestedRef.current) reloadPage();
    }),
    [botId],
  );

  const setBaseline = useCallback((workflowID: string, graph: BotConversationGraph) => {
    if (!workflowID) return;
    const json = JSON.stringify(graph);
    setGraphEntries((prev) => {
      const cur = prev[workflowID];
      const hasDraft = !!cur && cur.baseline !== null && cur.json !== cur.baseline;
      if (!hasDraft) {
        // Nothing unpublished — adopt what the server just gave us.
        if (cur && cur.json === json && cur.baseline === json) return prev;
        return { ...prev, [workflowID]: { graph, json, baseline: json } };
      }
      // Keep the unpublished edit; only move the baseline it's measured against.
      if (cur.baseline === json) return prev;
      return { ...prev, [workflowID]: { ...cur, baseline: json } };
    });
  }, []);

  const setDraft = useCallback((workflowID: string, graph: BotConversationGraph) => {
    if (!workflowID) return;
    const json = JSON.stringify(graph);
    setGraphEntries((prev) => {
      const cur = prev[workflowID];
      if (cur && cur.json === json) return prev; // no-op: identical state
      return { ...prev, [workflowID]: { graph, json, baseline: cur?.baseline ?? null } };
    });
  }, []);

  const commit = useCallback((workflowIDs: string[]) => {
    if (!workflowIDs.length) return;
    setGraphEntries((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const id of workflowIDs) {
        const cur = next[id];
        if (!cur || cur.baseline === cur.json) continue;
        next[id] = { ...cur, baseline: cur.json };
        changed = true;
      }
      return changed ? next : prev;
    });
  }, []);

  const graphs = useMemo(() => {
    const out: Record<string, BotConversationGraph> = {};
    for (const [id, entry] of Object.entries(graphEntries)) out[id] = entry.graph;
    return out;
  }, [graphEntries]);

  const dirtyIDs = useMemo(
    () =>
      Object.keys(graphEntries).filter((id) => {
        const e = graphEntries[id];
        return e.baseline !== null && e.json !== e.baseline;
      }),
    [graphEntries],
  );

  // Parse cache keyed by baseline JSON, so re-renders don't re-parse graphs.
  const baselineParseRef = useRef(new Map<string, BotConversationGraph>());
  const baselines = useMemo(() => {
    const cache = baselineParseRef.current;
    const next = new Map<string, BotConversationGraph>();
    const out: Record<string, BotConversationGraph> = {};
    for (const id of dirtyIDs) {
      const json = graphEntries[id].baseline as string;
      let parsed = cache.get(json);
      if (!parsed) {
        try { parsed = JSON.parse(json) as BotConversationGraph; } catch { continue; }
      }
      next.set(json, parsed);
      out[id] = parsed;
    }
    baselineParseRef.current = next;
    return out;
  }, [dirtyIDs, graphEntries]);

  const graphDrafts = useMemo<GraphDraftStore>(
    () => ({ graphs, dirtyIDs, dirty: dirtyIDs.length > 0, baselines, setBaseline, setDraft, commit }),
    [graphs, dirtyIDs, baselines, setBaseline, setDraft, commit],
  );

  return useMemo(
    () => ({ ctl, ensureLoaded, graphDrafts }),
    [ctl, ensureLoaded, graphDrafts],
  );
}

interface BotConfigRegistryValue {
  entries: Record<string, BotConfigContextValue>;
  ensureBot: (botId: string) => void;
}

const RegistryCtx = createContext<BotConfigRegistryValue | null>(null);

/**
 * One persistent hook host per bot touched during this shell session. Hosts
 * remain mounted when the route changes, so Bot X's unpublished draft can keep
 * backing an open Sidekick while the page underneath moves to Bot Y.
 */
const BotDraftHost = memo(function BotDraftHost({
  botId,
  register,
}: {
  botId: string;
  register: (botId: string, value: BotConfigContextValue) => void;
}) {
  const value = useBotConfigValue(botId);
  useLayoutEffect(() => {
    register(botId, value);
  }, [botId, register, value]);
  return null;
});

/**
 * Shell-lifetime draft registry. It does not fetch merely because a bot route
 * exists: each entry preserves the old lazy-load rule and only fetches when
 * Configure or a bot-scoped Sidekick asks for the config.
 */
export function BotConfigRegistryProvider({ children }: { children: React.ReactNode }) {
  const [botIds, setBotIds] = useState<string[]>([]);
  const [entries, setEntries] = useState<Record<string, BotConfigContextValue>>({});

  const ensureBot = useCallback((rawBotId: string) => {
    const botId = rawBotId.trim();
    if (!botId) return;
    setBotIds((prev) => (prev.includes(botId) ? prev : [...prev, botId]));
  }, []);

  const register = useCallback((botId: string, value: BotConfigContextValue) => {
    setEntries((prev) => (prev[botId] === value ? prev : { ...prev, [botId]: value }));
  }, []);

  const registry = useMemo(() => ({ entries, ensureBot }), [entries, ensureBot]);
  return (
    <RegistryCtx.Provider value={registry}>
      {botIds.map((botId) => <BotDraftHost key={botId} botId={botId} register={register} />)}
      {children}
    </RegistryCtx.Provider>
  );
}

function RegisteredBotConfigProvider({
  botId,
  children,
}: {
  botId: string;
  children: React.ReactNode;
}) {
  const registry = useContext(RegistryCtx);
  useEffect(() => {
    registry?.ensureBot(botId);
  }, [registry, botId]);
  const value = registry?.entries[botId] ?? null;
  if (!value) {
    return <main className="page-body" style={{ padding: 48, color: 'var(--ink-3)' }}>Loading…</main>;
  }
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

/**
 * Route scope for a bot draft. Under ShellLayout it binds to the persistent
 * registry; outside that shell (notably isolated tests) it keeps the original
 * standalone-provider behaviour.
 */
export function BotConfigProvider({ botId, children }: { botId: string; children: React.ReactNode }) {
  const registry = useContext(RegistryCtx);
  if (registry) {
    return <RegisteredBotConfigProvider botId={botId}>{children}</RegisteredBotConfigProvider>;
  }
  return <StandaloneBotConfigProvider botId={botId}>{children}</StandaloneBotConfigProvider>;
}

function StandaloneBotConfigProvider({
  botId,
  children,
}: {
  botId: string;
  children: React.ReactNode;
}) {
  const value = useBotConfigValue(botId);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

/**
 * Resolve and load a bot draft without changing the route's current bot scope.
 * This is the global Sidekick seam: an open X panel can continue editing and
 * publishing X while the visible route is Y.
 */
export function useBotConfigForSidekick(
  botId?: string,
  load = true,
): UseBotConfigResult | null {
  const registry = useContext(RegistryCtx);
  useEffect(() => {
    if (botId) registry?.ensureBot(botId);
  }, [registry, botId]);
  const value = botId && registry ? registry.entries[botId] : null;
  useEffect(() => {
    if (load) value?.ensureLoaded();
  }, [value, load]);
  return value?.ctl ?? null;
}

/**
 * Consume the hoisted bot config. Triggers the (single) lazy fetch on mount.
 * Throws if used outside a BotConfigProvider so misuse is caught immediately.
 */
export function useBotConfigContext(): UseBotConfigResult {
  const v = useContext(Ctx);
  if (!v) throw new Error('useBotConfigContext must be used within a BotConfigProvider');
  const { ctl, ensureLoaded } = v;
  useEffect(() => { ensureLoaded(); }, [ensureLoaded]);
  return ctl;
}

/**
 * Consume the unpublished graph drafts. Unlike useBotConfigContext this does
 * NOT trigger the config fetch — callers that only care about graph dirtiness
 * shouldn't force a config request.
 */
export function useGraphDrafts(): GraphDraftStore {
  const v = useContext(Ctx);
  if (!v) throw new Error('useGraphDrafts must be used within a BotConfigProvider');
  return v.graphDrafts;
}
